import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="menu-search"
export default class extends Controller {
  static targets = ["input", "dropdown", "results", "item", "empty", "clearButton", "shortcutBadge"]

  connect() {
    console.log("MenuSearchController connected successfully")
    this.activeIndex = -1
    // Bind click outside event
    this.clickOutsideHandler = this.clickOutside.bind(this)
    document.addEventListener("click", this.clickOutsideHandler)

    // Keyboard shortcut to focus search input: '/' or 'Cmd+K' / 'Ctrl+K'
    this.shortcutHandler = this.handleShortcut.bind(this)
    document.addEventListener("keydown", this.shortcutHandler)
  }

  disconnect() {
    console.log("MenuSearchController disconnected")
    document.removeEventListener("click", this.clickOutsideHandler)
    document.removeEventListener("keydown", this.shortcutHandler)
  }

  onFocus() {
    console.log("MenuSearchController focused")
    this.showDropdown()
  }

  onBlur() {
    console.log("MenuSearchController blurred")
    // Timeout to allow clicking links before hiding dropdown
    setTimeout(() => {
      this.hideDropdown()
    }, 200)
  }

  onInput() {
    const query = this.inputTarget.value.trim().toLowerCase()
    console.log("MenuSearchController input changed:", query)
    this.updateControls()
    
    if (query === "") {
      this.resetFilter()
      return
    }

    let matchCount = 0
    this.itemTargets.forEach((item) => {
      const searchText = item.dataset.searchText || ""
      const isMatch = searchText.includes(query)
      item.classList.toggle("hidden", !isMatch)
      item.setAttribute("aria-selected", "false")
      if (isMatch) matchCount++
    })

    this.emptyTarget.classList.toggle("hidden", matchCount > 0)
    this.activeIndex = -1
    this.showDropdown()
  }

  onKeyDown(event) {
    const visibleItems = this.getVisibleItems()
    if (visibleItems.length === 0) return

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.navigate(1, visibleItems)
        break
      case "ArrowUp":
        event.preventDefault()
        this.navigate(-1, visibleItems)
        break
      case "Enter":
        if (this.activeIndex >= 0 && this.activeIndex < visibleItems.length) {
          event.preventDefault()
          visibleItems[this.activeIndex].click()
        }
        break
      case "Escape":
        event.preventDefault()
        this.inputTarget.blur()
        this.hideDropdown()
        break
    }
  }

  getVisibleItems() {
    return this.itemTargets.filter(item => !item.classList.contains("hidden"))
  }

  navigate(direction, visibleItems) {
    // Clear previous selection
    if (this.activeIndex >= 0 && this.activeIndex < visibleItems.length) {
      visibleItems[this.activeIndex].setAttribute("aria-selected", "false")
    }

    // Update index
    this.activeIndex += direction

    if (this.activeIndex >= visibleItems.length) {
      this.activeIndex = 0
    } else if (this.activeIndex < 0) {
      this.activeIndex = visibleItems.length - 1
    }

    // Set new selection
    const activeItem = visibleItems[this.activeIndex]
    activeItem.setAttribute("aria-selected", "true")

    // Scroll active item into view
    activeItem.scrollIntoView({ block: "nearest" })
  }

  resetFilter() {
    this.itemTargets.forEach((item) => {
      item.classList.remove("hidden")
      item.setAttribute("aria-selected", "false")
    })
    this.emptyTarget.classList.add("hidden")
    this.activeIndex = -1
  }

  showDropdown() {
    this.dropdownTarget.classList.remove("hidden")
  }

  hideDropdown() {
    this.dropdownTarget.classList.add("hidden")
    this.resetSelection()
  }

  resetSelection() {
    const visibleItems = this.getVisibleItems()
    if (this.activeIndex >= 0 && this.activeIndex < visibleItems.length) {
      visibleItems[this.activeIndex].setAttribute("aria-selected", "false")
    }
    this.activeIndex = -1
  }

  clear() {
    this.inputTarget.value = ""
    this.resetFilter()
    this.updateControls()
    this.inputTarget.focus()
  }

  updateControls() {
    const hasQuery = this.inputTarget.value.trim().length > 0
    if (this.hasClearButtonTarget) this.clearButtonTarget.classList.toggle("hidden", !hasQuery)
    if (this.hasShortcutBadgeTarget) this.shortcutBadgeTarget.classList.toggle("hidden", hasQuery)
  }

  clickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideDropdown()
    }
  }

  handleShortcut(event) {
    // Don't trigger if user is focusing an input, textarea, or select element
    const activeEl = document.activeElement
    if (activeEl && (
      activeEl.tagName === "INPUT" ||
      activeEl.tagName === "TEXTAREA" ||
      activeEl.tagName === "SELECT" ||
      activeEl.isContentEditable
    )) {
      return
    }

    if (event.key === "/" || ((event.metaKey || event.ctrlKey) && event.key === "k")) {
      event.preventDefault()
      this.inputTarget.focus()
      this.showDropdown()
    }
  }
}
